(() => {
  if (customElements.get('mv-solar-collection')) return;

  class MiravelSolarCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.loading = false;
      this.failed = false;
      this.controller = new AbortController();
      this.addEventListener('click', this.onClick = event => {
        const link = event.target.closest('[data-mv-next-page]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.failed = false;
        this.loadNext();
      });
      this.initialize();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.removeEventListener('click', this.onClick);
      this.initialized = false;
    }

    requestUrl(href) {
      const url = new URL(href, window.location.href);
      const current = new URL(window.location.href);
      for (const key of ['preview_theme_id', 'pb', '_ab', '_fd', '_sc']) {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      }
      if (this.dataset.bestsellers === 'true') url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url;
    }

    async fetchCollection(url) {
      const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const collection = document.querySelector('mv-solar-collection');
      if (!collection?.querySelector('#product-grid')) throw new Error('Collection response missing');
      return collection;
    }

    async initialize() {
      const currentPage = Number(new URL(window.location.href).searchParams.get('page') || 1);
      if (this.dataset.bestsellers === 'true' && (this.dataset.sort !== 'best-selling' || currentPage > 1)) {
        const url = this.requestUrl(window.location.href);
        url.searchParams.delete('page');
        this.setAttribute('aria-busy', 'true');
        try {
          const collection = await this.fetchCollection(url);
          if (collection.dataset.sort !== 'best-selling') throw new Error('Collection sort missing');
          this.replaceChildren(...collection.childNodes);
          this.dataset.sort = 'best-selling';
          const displayUrl = new URL(window.location.href);
          displayUrl.searchParams.set('sort_by', 'best-selling');
          displayUrl.searchParams.delete('page');
          window.history.replaceState(window.history.state, '', displayUrl);
        } catch (error) {
          if (error.name !== 'AbortError') {
            url.searchParams.delete('section_id');
            window.location.replace(url.toString());
          }
          return;
        } finally {
          this.removeAttribute('aria-busy');
        }
      }
      this.observeNext();
    }

    observeNext() {
      this.observer?.disconnect();
      const more = this.querySelector('[data-mv-collection-more]');
      if (!more || !('IntersectionObserver' in window)) return;
      this.dataset.mvEnhanced = 'true';
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.loading && !this.failed) this.loadNext();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(more);
    }

    async loadNext() {
      const link = this.querySelector('[data-mv-next-page]');
      const grid = this.querySelector('#product-grid');
      if (this.loading || !link || !grid) return;
      this.loading = true;
      const more = link.closest('[data-mv-collection-more]');
      const status = more.querySelector('[data-mv-collection-status]');
      more.removeAttribute('data-mv-load-error');
      link.setAttribute('aria-disabled', 'true');
      this.setAttribute('aria-busy', 'true');
      if (status) status.textContent = this.dataset.loadingLabel;
      try {
        const collection = await this.fetchCollection(this.requestUrl(link.href));
        if (this.dataset.bestsellers === 'true' && collection.dataset.sort !== 'best-selling') throw new Error('Collection sort missing');
        const seen = new Set([...grid.querySelectorAll('[data-mv-product-id]')].map(item => item.dataset.mvProductId));
        const items = [...collection.querySelector('#product-grid').children];
        for (const item of items) {
          const id = item.dataset.mvProductId;
          if (id && seen.has(id)) continue;
          if (id) seen.add(id);
          item.style.setProperty('--index', '0');
          grid.appendChild(item);
        }
        const next = collection.querySelector('[data-mv-next-page]');
        if (next) {
          const nextUrl = this.requestUrl(next.getAttribute('href'));
          nextUrl.searchParams.delete('section_id');
          link.href = nextUrl.toString();
          this.observeNext();
        } else {
          this.observer?.disconnect();
          more.remove();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.failed = true;
          more.setAttribute('data-mv-load-error', '');
          this.observer?.disconnect();
        }
      } finally {
        this.loading = false;
        link.removeAttribute('aria-disabled');
        this.removeAttribute('aria-busy');
        if (status) status.textContent = '';
      }
    }
  }

  customElements.define('mv-solar-collection', MiravelSolarCollection);
})();
